import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DRAWER_LINK_ITEMS } from '../lib/siteNavigation';
import { useCurrency } from '../contexts/CurrencyContext';
import { ALL_CURRENCIES, getCurrencyConfig } from '../lib/currency';

interface NavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onContactClick?: () => void;
  contactHref?: string;
  onPrimaryAction?: () => void;
  primaryActionLabel?: string;
  showPrimaryAction?: boolean;
}

interface MenuRowProps {
  label: string;
  to?: string;
  onClick?: () => void;
}

function MenuRow({ label, to, onClick }: MenuRowProps) {
  const content = (
    <>
      <span className="min-w-0 flex-1 text-left text-[15px] font-semibold tracking-tight text-white">
        {label}
      </span>
    </>
  );

  if (to) {
    return (
      <Link to={to} onClick={onClick} className="nav-drawer-link">
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className="nav-drawer-link">
      {content}
    </button>
  );
}

function CurrencyToggle() {
  const { currency, setCurrency } = useCurrency();

  return (
    <div className="mt-4 px-2">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-white/40">Currency</p>
      <div className="flex gap-1 rounded-xl bg-white/5 p-1">
        {ALL_CURRENCIES.map((code) => {
          const { symbol } = getCurrencyConfig(code);
          const isActive = code === currency;
          return (
            <button
              key={code}
              type="button"
              onClick={() => setCurrency(code)}
              className={`flex-1 rounded-lg py-2 text-center text-sm font-semibold transition ${
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-white/50 hover:text-white/70'
              }`}
            >
              {symbol} {code}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function NavDrawer({
  isOpen,
  onClose,
  onContactClick,
  contactHref,
  onPrimaryAction,
  primaryActionLabel = 'Start Quiz',
  showPrimaryAction = Boolean(onPrimaryAction),
}: NavDrawerProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');

      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? [],
      ).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (!active || active === first || !panelRef.current?.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (!active || active === last || !panelRef.current?.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" role="dialog" aria-modal="true" aria-label="Site menu">
      <div className="nav-drawer-backdrop absolute inset-0" onClick={onClose} />

      <div
        className="nav-drawer-panel relative h-full w-[85vw] max-w-[22rem] md:max-w-[20rem]"
        style={{
          paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
          paddingRight: 'calc(0.5rem + env(safe-area-inset-right, 0px))',
        }}
      >
        <div ref={panelRef} className="nav-drawer-surface mx-2 flex h-full flex-col rounded-[1.6rem] p-4 md:rounded-[1.4rem] md:p-4">
          <div className="mb-4 flex items-center justify-between gap-3">
            <Link
              to="/"
              onClick={onClose}
              className="flex min-w-0 items-center gap-2 rounded-2xl px-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            >
              <span className="text-xl" aria-hidden="true">🔥</span>
              <span className="truncate text-base font-extrabold tracking-tight text-white">OurSexQuiz</span>
            </Link>

            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-slate-200 transition hover:bg-white/8 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
              aria-label="Close menu"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="flex flex-col gap-1" aria-label="Main navigation">
            <MenuRow to="/" onClick={onClose} label="Home" />

            {DRAWER_LINK_ITEMS.map((item) => {
              return (
                <MenuRow
                  key={item.key}
                  to={item.to}
                  onClick={onClose}
                  label={item.label}
                />
              );
            })}

            {onContactClick && (
              <MenuRow
                label="Contact"
                onClick={() => {
                  onClose();
                  onContactClick();
                }}
              />
            )}

            {!onContactClick && contactHref && (
              <MenuRow to={contactHref} onClick={onClose} label="Contact" />
            )}
          </nav>

          <CurrencyToggle />

          <div className="mt-auto pt-5">
            {showPrimaryAction && onPrimaryAction && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onPrimaryAction();
                  }}
                  className="nav-drawer-cta gradient-cta w-full rounded-2xl px-4 py-5 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/80"
                >
                  <span className="block text-center text-lg font-extrabold tracking-tight">
                    {primaryActionLabel} <span aria-hidden="true">🔥</span>
                  </span>
                </button>
                <p className="mt-3 text-center text-xs font-medium text-white/65">
                  Find your shared yeses
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
